import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { MenuAddonItem, MenuOption } from "../hooks/useMenu";
import { v4 as uuidv4 } from "uuid";
import { Condiment } from "@/components/ProductPane";
import { MenuType } from "@/types/menu";

export interface CartItem extends MenuOption {
  lineId: string;
  count: number;
  addons?: (MenuAddonItem & { count: number })[];
  cartCondiment: (Condiment & { count: number })[];
  isUpsale?: boolean;
}

export interface RemovedItem {
  id: string;
  name: string;
  removedAt: Date;
}

type CartStore = {
  locationId: string;
  cart: CartItem[];
  removedItems: RemovedItem[];
  table?: string;
  price: number;
  locationName: string;
  setLocationId: (id: string) => void;
  setLocationName: (name: string) => void;
  setTable: (table: string) => void;
  resetTable: () => void;
  count: () => number;
  setCount: (lineId: string, count: number) => void;
  add: (
    menuItem: MenuOption & {
      isUpsale?: boolean;
      addons?: (MenuAddonItem & { count: number })[];
      cartCondiment: (Condiment & { count: number })[];
    },
    quantity?: number
  ) => void;
  remove: (lineId: string) => void; // Assuming id is a string
  removeAll: () => void;
  addRemovedItem: (name: string) => void;
  dismissRemovedItem: (id: string) => void;
  clearRemovedItems: () => void;
  mode: MenuType | null;
  setMode: (mode: MenuType | null) => void;
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      locationId: "",
      cart: [],
      removedItems: [],
      price: 0,
      locationName: "",
      setLocationName: (name: string) => set({ locationName: name }),
      setLocationId: (id: string) => set({ locationId: id }),
      setTable: (table: string) => set({ table }),
      resetTable: () => set({ table: undefined }),
      count: () => {
        const { cart } = get();
        return cart.reduce((total, item) => total + item.count, 0);
      },
      setCount: (lineId: string, count: number) => {
        const { cart } = get();
        const updatedCart = setCount(lineId, cart, count);
        const newPrice = calculateTotalPrice(updatedCart);
        set({ cart: updatedCart, price: newPrice });
      },
      add: (
        menuItem: MenuOption & {
          isUpsale?: boolean;
          addons?: (MenuAddonItem & { count: number })[];
          cartCondiment: (Condiment & { count: number })[];
        },
        quantity?: number
      ) => {
        const { cart } = get();

        const updatedCart = updateCart(menuItem, quantity ?? 1, cart);
        const newPrice = calculateTotalPrice(updatedCart);
        set({ cart: updatedCart, price: newPrice });
      },
      remove: (lineId: string) => {
        const { cart } = get();
        const updatedCart = removeCart(lineId, cart);
        const newPrice = calculateTotalPrice(updatedCart);
        set({ cart: updatedCart, price: newPrice });
      },
      removeAll: () => set({ cart: [], price: 0 }),
      addRemovedItem: (name: string) => {
        const { removedItems } = get();
        const newRemovedItem: RemovedItem = {
          id: uuidv4(),
          name,
          removedAt: new Date()
        };
        set({ removedItems: [...removedItems, newRemovedItem] });
      },
      dismissRemovedItem: (id: string) => {
        const { removedItems } = get();
        set({ removedItems: removedItems.filter(item => item.id !== id) });
      },
      clearRemovedItems: () => set({ removedItems: [] }),
      mode: null,
      setMode: (mode: MenuType | null) => set({ mode }),
    }),
    {
      name: "cart-storage",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

function calculateTotalPrice(cart: CartItem[]): number {
  const getAddonPriceForItem = (item: CartItem): number => {
    if (
      !item.addons ||
      !Array.isArray(item.addons) ||
      item.addons.length === 0
    ) {
      return 0;
    }
    return item.addons.reduce((total, addon) => {
      const price = typeof addon.price === "number" ? addon.price : 0;
      const count = typeof addon.count === "number" ? addon.count : 0;
      return total + price * count;
    }, 0);
  };

  const getCondimentPriceForItem = (item: CartItem): number => {
    if (
      !item.cartCondiment ||
      !Array.isArray(item.cartCondiment) ||
      item.cartCondiment.length === 0
    ) {
      return 0;
    }
    return item.cartCondiment.reduce((total, condiment) => {
      const price = typeof condiment.price === "number" ? condiment.price : 0;
      const count = typeof condiment.count === "number" ? condiment.count : 0;
      return total + price * count;
    }, 0);
  };

  const total = cart.reduce((currentTotal, item) => {
    const basePrice = typeof item.price === "number" ? item.price : 0;
    const quantity = typeof item.count === "number" ? item.count : 0;

    const baseItemTotal = basePrice * quantity;
    const addonsTotal = getAddonPriceForItem(item) * quantity;
    const condimentsTotal = getCondimentPriceForItem(item) * quantity;

    return currentTotal + baseItemTotal + addonsTotal + condimentsTotal;
  }, 0);
  return parseFloat(total.toFixed(2));
}

function setCount(lineId: string, cart: CartItem[], count: number): CartItem[] {
  const existingItemIndex = cart.findIndex((item) => item.lineId === lineId);

  if (count === 0) {
    return cart.filter((item) => item.lineId !== lineId);
  } else {
    cart[existingItemIndex].count = count;
  }

  return [...cart];
}

function updateCart(
  menuItem: MenuOption & {
    isUpsale?: boolean;
    addons?: (MenuAddonItem & { count: number })[];
    cartCondiment: (Condiment & { count: number })[];
  },
  quantity: number,
  cart: CartItem[]
): CartItem[] {
  const cartItem = {
    ...menuItem,
    count: quantity,
    lineId: uuidv4().toString(),
  };

  cart.push(cartItem);

  return [...cart];
}

function removeCart(lineId: string, cart: CartItem[]): CartItem[] {
  return cart.reduce((newCart, item) => {
    if (item.lineId === lineId) {
      if (item.count > 1) {
        newCart.push({ ...item, count: item.count - 1 });
      }
    } else {
      newCart.push(item);
    }
    return newCart;
  }, [] as CartItem[]);
}
